import { DEMO_TARGET_ATTRIBUTE } from 'constants/demoTarget.constants';
import { CustomText } from 'components/CustomText';
import { SectionContainer, SectionHeader } from './Section.styles';

interface SectionProps {
  title?: string;
  children: JSX.Element;
  block?: boolean;
  dataTestId?: string;
  dataDemoTarget?: string;
}

/**
 * Renders a bordered section with an optional title and children.
 *
 * The `title` prop is currently rendered via `CustomText`.
 *
 * **Convention for future usage:** if you add a new `title`-like prop whose value
 * comes from a PTP Config custom text field, then the field should have tooltip saying
 * that the field supports HTML from the start.
 *
 * @note This gives `Section` awareness of `CustomText`, which is a minor SRP bypass.
 * Accepted trade-off to avoid callsite churn while keeping the HTML rendering centralised.
 */
const Section = ({
  title,
  block = false,
  children,
  dataTestId,
  dataDemoTarget,
}: SectionProps): JSX.Element => {
  const testIdDataAttribute = dataTestId ? { 'data-testid': dataTestId } : {};
  const demoTargetAttribute = dataDemoTarget
    ? { [DEMO_TARGET_ATTRIBUTE]: dataDemoTarget }
    : {};
  const titleTestIdDataAttribute = dataTestId
    ? { 'data-testid': `${dataTestId}-title` }
    : {};

  return (
    <SectionContainer
      block={block}
      {...testIdDataAttribute}
      {...demoTargetAttribute}
    >
      {title && (
        <CustomText
          as={SectionHeader}
          text={title}
          {...titleTestIdDataAttribute}
        />
      )}
      {children}
    </SectionContainer>
  );
};

export { Section };
